import React, { useState, useEffect } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isLoaded, setIsLoaded] = useState(false);

  const [headerRef, headerVisible] = useScrollAnimation(0.3);
  const [leftRef, leftVisible] = useScrollAnimation(0.2);
  const [rightRef, rightVisible] = useScrollAnimation(0.2);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Create email content
      const emailContent = `
New Contact Form Submission from Brand Aviation Website

Contact Information:
- Name: ${formData.name}
- Email: ${formData.email}

Message:
${formData.message}

---
This message was sent from the Brand Aviation website contact form.
      `;

      // Create mailto link
      const subject = encodeURIComponent(`New Contact Form Submission from ${formData.name}`);
      const body = encodeURIComponent(emailContent);
      const mailtoLink = `mailto:info@brandaviation.com?subject=${subject}&body=${body}`;

      // Open email client
      window.location.href = mailtoLink;
      
      // Show success message
      alert('Thank you for your message! Your email client will open to send us your message.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact us directly at info@brandaviation.com');
    }
  };

  const contactMethods = [
    {
      icon: '📧',
      title: 'Email',
      value: 'info@brandaviation.com',
      description: 'Drop us a line anytime'
    },
    {
      icon: '📱',
      title: 'Phone',
      value: '0314-5154682',
      description: 'Call us for urgent matters'
    },
    {
      icon: '📍',
      title: 'Office',
      value: 'Blue Area, Islamabad',
      description: 'Visit our creative hub'
    }
  ];

  return (
    <section id="contact" className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-white">
        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="h-full w-full" style={{
            backgroundImage: `
              linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-brand-orange/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-12 h-12 bg-brand-gold/15 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute bottom-40 left-1/4 w-20 h-20 bg-brand-orange/5 rounded-full blur-2xl animate-ping"></div>
        <div className="absolute bottom-20 right-1/3 w-8 h-8 bg-brand-gold/20 rounded-full blur-md animate-pulse"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 sm:mb-20 transition-all duration-1000 ${
            headerVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Badge */}
          <div className={`inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border border-brand-orange/20 rounded-full px-4 py-2 text-brand-orange text-sm font-medium mb-6 transition-all duration-1000 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.1s' }}>
            <div className="w-2 h-2 bg-brand-orange rounded-full animate-pulse"></div>
            <span>Let's Connect</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 sm:mb-8 transition-all duration-700">
            <span className={`block transition-all duration-500 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.2s' }}>
              Contrary to our name,
            </span>
            <span className={`block bg-gradient-to-r from-brand-orange to-brand-gold bg-clip-text text-transparent transition-all duration-500 ${
              isLoaded ? 'animate-fade-in-up' : ''
            }`} style={{ animationDelay: '0.4s' }}>
              we're easy to reach.
            </span>
          </h2>
          
          <div className={`w-24 h-1 bg-gradient-to-r from-brand-orange to-brand-gold mx-auto mb-6 sm:mb-8 transition-all duration-500 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.6s' }}></div>
          
          <p className={`text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed transition-all duration-500 ${
            isLoaded ? 'animate-fade-in-up' : ''
          }`} style={{ animationDelay: '0.8s' }}>
            …but you might have to serenade us a little over email first. 
            Just email us with a brief intro and we'll get back to you pronto.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Enhanced Contact Info */}
          <div 
            ref={leftRef}
            className={`space-y-8 transition-all duration-1000 ${
              leftVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 -translate-x-10'
            }`}
          >
            {/* Contact Methods Card */}
            <div className="relative overflow-hidden">
              {/* Card Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/30 to-brand-orange/30 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110"></div>
              
              {/* Main Card */}
              <div className="relative bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-orange rounded-3xl p-8 sm:p-10 text-white overflow-hidden">
                {/* Background Elements */}
                <div className="absolute top-6 left-6 w-16 h-16 bg-white/10 rounded-full blur-lg animate-pulse"></div>
                <div className="absolute bottom-6 right-6 w-12 h-12 bg-white/15 rounded-full blur-md animate-bounce"></div>
                <div className="absolute top-1/2 left-1/4 w-8 h-8 bg-white/5 rounded-full blur-xl animate-ping"></div>

                <div className="relative z-10">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-8 transition-all duration-500">
                    Get in Touch
                  </h3>
                  
                  <div className="space-y-6">
                    {contactMethods.map((method, index) => (
                      <div key={method.title} className="group flex items-center space-x-4 transition-all duration-300 hover:translate-x-2">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-2xl flex items-center justify-center text-xl sm:text-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 shadow-lg">
                          {method.icon}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-sm sm:text-base text-white group-hover:text-brand-orange transition-colors duration-300">
                            {method.title}
                          </p>
                          <p className="text-white/90 text-sm sm:text-base font-medium">
                            {method.value}
                          </p>
                          <p className="text-white/70 text-xs sm:text-sm">
                            {method.description}
                          </p>
                        </div>
                        <div className="w-2 h-2 bg-brand-orange rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Careers Card */}
            <div className="relative overflow-hidden">
              {/* Card Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 to-brand-gold/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110"></div>
              
              {/* Main Card */}
              <div className="relative bg-white/95 backdrop-blur-sm border border-white/30 rounded-3xl p-8 sm:p-10 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-orange/20 group-hover:scale-105">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-brand-orange to-brand-gold rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 shadow-lg">
                    🦸
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 transition-all duration-500">
                    Want to join our cause?
                  </h4>
                </div>
                
                <div className="space-y-4 text-center">
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed transition-all duration-500">
                    We're always on the lookout for superheroes/rebels to join our cause and help save 
                    the nation from boring campaigns in the garb of 'marketing'.
                  </p>
                  <p className="text-gray-600 text-sm sm:text-base leading-relaxed transition-all duration-500">
                    Potential candidates must possess super sharp wit, ability to look into the future, 
                    problem-solving skills, and extremely thick skin.
                  </p>
                  <p className="text-xs sm:text-sm text-brand-orange font-semibold transition-all duration-500">
                    Wearing a cape is optional.
                  </p>
                </div>
                
                {/* Corner Accents */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-brand-orange/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-300"></div>
                <div className="absolute bottom-4 left-4 w-1.5 h-1.5 bg-brand-gold/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-500 delay-200"></div>
              </div>
            </div>
          </div>

          {/* Enhanced Contact Form */}
          <div 
            ref={rightRef}
            className={`transition-all duration-1000 ${
              rightVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="relative overflow-hidden">
              {/* Card Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-brand-orange/20 to-brand-gold/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500 -z-10 scale-110"></div>
              
              {/* Main Card */}
              <div className="relative bg-white/95 backdrop-blur-sm border border-white/30 rounded-3xl p-8 sm:p-10 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-orange/20 group-hover:scale-105">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-brand-orange to-brand-gold rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 shadow-lg">
                    ✉️
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 transition-all duration-500">
                    Get In Touch With Us
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base transition-all duration-500">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-3 transition-all duration-300">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 text-sm sm:text-base shadow-sm hover:shadow-md"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-3 transition-all duration-300">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 text-sm sm:text-base shadow-sm hover:shadow-md"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-3 transition-all duration-300">
                      How can we help you?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all duration-300 text-sm sm:text-base resize-none shadow-sm hover:shadow-md"
                      required
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="group relative w-full overflow-hidden"
                  >
                    {/* Main Button */}
                    <div className="relative bg-gradient-to-r from-brand-orange via-brand-orange-light to-brand-gold text-white py-4 rounded-xl font-bold text-base sm:text-lg shadow-xl shadow-brand-orange/25 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-brand-orange/40 group-hover:scale-105">
                      <span className="relative z-10 flex items-center justify-center space-x-3">
                        <span>Send Message</span>
                        <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </span>
                      
                      {/* Shimmer Effect */}
                      <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    </div>
                    
                    {/* Floating Elements */}
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-brand-gold/30 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-300"></div>
                    <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-brand-orange/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-500 delay-100"></div>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
